import React from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  TableRow,
  TextField,
  Tooltip
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import * as customColors from "../../../../../theme/customColors";
import DatePickerField from "../../shared/ui/HaklaiDatePickerField";
import { COMPACT_CURRENCY_LABELS } from "../../shared/haklaiConstants";
import {
  formatHaklaiClassicMoneyInput,
  normalizeHaklaiClassicMoneyInputOnBlur
} from "../../shared/haklaiMoneyHelpers";
import {
  HaklaiStyledCell as StyledCell,
  FieldShell,
  buildFieldErrorId,
  renderFieldErrorIndicator
} from "../../shared/table/haklaiTableShared";
import {
  getHaklaiZabanAttributionSelections,
  buildHaklaiZabanAttributionValue
} from "../../shared/haklaiFlowHelpers";
import {
  HAKLAI_EXPENSE_ERRORS,
  HAKLAI_EXPENSE_LABELS,
  HAKLAI_EXPENSE_TOOLTIPS
} from "../haklaiExpenseTexts";
import { DATE_INPUT_WIDTH } from "../haklaiExpensesStyles";
import HaklaiSectionLabelCell from "../../shared/ui/HaklaiSectionLabelCell";

// Frozen empty object used as a stable fallback for per-row cell errors.
const EMPTY_ROW_FIELDS = Object.freeze({});

const HaklaiExpenseRow = React.memo(({
  expense,
  isIgnorableTrailingRow,
  validationState,
  cellErrors,
  isFlashing,
  classes,
  showZabanColumn,
  showDepreciationColumns,
  expenseTypeValueSet,
  expenseTypeOptions,
  currencyOptions,
  zabanCheckboxOptions,
  handleExpenseFieldChange,
  touchRowField,
  handleAmountChange,
  handleRemoveExpense,
  setFieldRef,
  setRowRef,
  expenseTypeInputRefs,
  amountInputRefs,
  currencyInputRefs,
  zabanInputRefs,
  renderExpenseDepreciationField,
  renderExpenseDepreciationResultCell,
  renderNonRelevantCell
}) => {
  const selectedZabanValues = getHaklaiZabanAttributionSelections(expense.zabanAttribution);
  const rowCellErrors = cellErrors || EMPTY_ROW_FIELDS;
  const hasExpenseTypeError = Boolean(rowCellErrors.expenseType);
  const hasAmountError = Boolean(rowCellErrors.amount);
  const hasCurrencyError = Boolean(rowCellErrors.expenseCurrency);
  const hasDateError = Boolean(rowCellErrors.expenseDate);
  const hasZabanError = Boolean(rowCellErrors.zabanAttribution);
  // Per-cell id for `aria-describedby` wiring. Mirrors the property-parts
  // table's contract so AT reads the full error sentence alongside the
  // focused input.
  const expenseTypeErrorId = buildFieldErrorId(expense.id, "expenseType");
  const amountErrorId = buildFieldErrorId(expense.id, "amount");
  const currencyErrorId = buildFieldErrorId(expense.id, "expenseCurrency");
  const dateErrorId = buildFieldErrorId(expense.id, "expenseDate");
  const zabanErrorId = buildFieldErrorId(expense.id, "zabanAttribution");

  const matchedExpenseType = expenseTypeValueSet.has(expense.expenseType) ? expense.expenseType : "";
  const legacyExpenseTypeOption = expense.expenseType && !matchedExpenseType
    ? { value: expense.expenseType, label: expense.expenseType, isLegacy: true }
    : null;
  const resolvedExpenseTypeOptions = legacyExpenseTypeOption
    ? [legacyExpenseTypeOption, ...expenseTypeOptions]
    : expenseTypeOptions;
  const selectedExpenseTypeValue = matchedExpenseType || legacyExpenseTypeOption?.value || "";
  const selectedExpenseTypeLabel = resolvedExpenseTypeOptions
    .find((option) => option.value === selectedExpenseTypeValue)?.label || "";

  return (
    <TableRow
      key={expense.id}
      ref={setRowRef ? setRowRef(expense.id) : undefined}
      className={[classes.expenseDataRow, isFlashing ? classes.rowFlash : ""].filter(Boolean).join(" ")}
    >
      <HaklaiSectionLabelCell />
      <StyledCell align="center">
        <Box className={`${classes.columnFieldWrap} ${classes.expenseTypeFieldWrap}`}>
          <FieldShell
            classes={classes}
            shellClassName={classes.expenseTypeFieldShell}
            errorMsg={rowCellErrors.expenseType || ""}
            errorId={expenseTypeErrorId}
          >
            <Tooltip title={selectedExpenseTypeLabel} disableHoverListener={!selectedExpenseTypeLabel}>
              <TextField
                name={`expenseType_${expense.id}`}
                select
                variant="outlined"
                size="small"
                className={[
                  classes.expenseTypeSelectInput,
                  isIgnorableTrailingRow ? classes.optionalTrailingField : ""
                ].filter(Boolean).join(" ")}
                value={selectedExpenseTypeValue}
                onChange={(e) => handleExpenseFieldChange(expense.id, "expenseType", e.target.value)}
                onBlur={() => touchRowField(expense.id, "expenseType")}
                error={hasExpenseTypeError}
                inputRef={setFieldRef(expenseTypeInputRefs, expense.id)}
                inputProps={{
                  "aria-invalid": hasExpenseTypeError,
                  "aria-describedby": hasExpenseTypeError ? expenseTypeErrorId : undefined
                }}
                SelectProps={{
                  displayEmpty: true,
                  SelectDisplayProps: {
                    "aria-invalid": hasExpenseTypeError,
                    "aria-describedby": hasExpenseTypeError ? expenseTypeErrorId : undefined
                  },
                  MenuProps: {
                    PaperProps: { className: "haklai-select-menu" }
                  },
                  renderValue: (selected) => {
                    if (!selected) {
                      return (
                        <span className={classes.expenseTypeValue} style={{ color: "#9e9e9e", fontWeight: 400 }}>
                          {HAKLAI_EXPENSE_LABELS.expenseTypePlaceholder}
                        </span>
                      );
                    }
                    const option = resolvedExpenseTypeOptions.find((opt) => opt.value === selected);
                    return <span className={classes.expenseTypeValue}>{option?.label || selected}</span>;
                  }
                }}
              >
                {resolvedExpenseTypeOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    disabled={option.isLegacy === true}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Tooltip>
          </FieldShell>
        </Box>
      </StyledCell>
      <StyledCell align="center">
        <Box className={`${classes.columnFieldWrap} ${classes.dateFieldWrap}`}>
          <FieldShell
            classes={classes}
            shellClassName={classes.dateFieldShell}
            errorMsg={rowCellErrors.expenseDate || ""}
            errorId={dateErrorId}
          >
            {/*
              `suppressHelperText` defers every error message to the FieldShell
              indicator (the red "!"). The picker still applies its red outline
              via `error={hasDateError}`. The internal `localValidationError`
              path still clears bad input on blur, which the overlay picks up
              as an empty-date required error.
            */}
            {/*
              `suppressHelperText` keeps the picker from rendering its own
              `FormHelperText` — the shell's red "!" is the single error
              surface for this cell. `inputProps.aria-describedby` points
              the screen-reader at the shell's `visuallyHidden` span (same
              a11y contract as every other cell in both haklai tables).
            */}
            <DatePickerField
              name={`expenseDate_${expense.id}`}
              className={[classes.dateInput, "haklai-date-input"].join(" ")}
              textFieldWidth={DATE_INPUT_WIDTH}
              exactWidth
              value={expense.expenseDate || ""}
              invalidInputMessage={HAKLAI_EXPENSE_ERRORS.expenseDateInvalid}
              validateInvalidInputOnBlur
              suppressHelperText
              onChange={(e) => handleExpenseFieldChange(expense.id, "expenseDate", e.target.value)}
              onBlur={() => touchRowField(expense.id, "expenseDate")}
              error={hasDateError}
              inputProps={{
                "aria-invalid": hasDateError,
                "aria-describedby": hasDateError ? dateErrorId : undefined
              }}
            />
          </FieldShell>
        </Box>
      </StyledCell>
      <StyledCell align="center">
        <Box className={`${classes.columnFieldWrap} ${classes.amountFieldWrap}`}>
          <FieldShell
            classes={classes}
            shellClassName={classes.amountFieldShell}
            errorMsg={rowCellErrors.amount || ""}
            errorId={amountErrorId}
          >
            <TextField
              name={`expenseAmount_${expense.id}`}
              type="text"
              variant="outlined"
              size="small"
              className={[
                classes.amountInput,
                "haklai-money-input",
                isIgnorableTrailingRow ? classes.optionalTrailingField : ""
              ].filter(Boolean).join(" ")}
              value={formatHaklaiClassicMoneyInput(expense.amount)}
              onChange={(e) => handleAmountChange(expense.id, e.target.value)}
              onBlur={() => {
                const normalizedAmount = normalizeHaklaiClassicMoneyInputOnBlur(expense.amount);
                if (normalizedAmount !== expense.amount) {
                  handleAmountChange(expense.id, normalizedAmount);
                }
                touchRowField(expense.id, "amount");
              }}
              error={hasAmountError}
              inputRef={setFieldRef(amountInputRefs, expense.id)}
              inputProps={{
                "aria-invalid": hasAmountError,
                "aria-describedby": hasAmountError ? amountErrorId : undefined
              }}
            />
          </FieldShell>
        </Box>
      </StyledCell>
      <StyledCell align="center">
        <Box className={`${classes.columnFieldWrap} ${classes.currencyFieldWrap}`}>
          <FieldShell
            classes={classes}
            shellClassName={classes.currencyFieldShell}
            errorMsg={rowCellErrors.expenseCurrency || ""}
            errorId={currencyErrorId}
          >
            <TextField
              name={`expenseCurrency_${expense.id}`}
              select
              variant="outlined"
              size="small"
              className={[
                "haklai-currency-field",
                classes.currencySelectInput,
                classes.editableCurrencySelectInput
              ].join(" ")}
              value={expense.expenseCurrency || "newShekel"}
              onChange={(e) => handleExpenseFieldChange(expense.id, "expenseCurrency", e.target.value)}
              onBlur={() => touchRowField(expense.id, "expenseCurrency")}
              error={hasCurrencyError}
              inputRef={setFieldRef(currencyInputRefs, expense.id)}
              inputProps={{
                "aria-invalid": hasCurrencyError,
                "aria-describedby": hasCurrencyError ? currencyErrorId : undefined
              }}
              SelectProps={{
                SelectDisplayProps: {
                  "aria-invalid": hasCurrencyError,
                  "aria-describedby": hasCurrencyError ? currencyErrorId : undefined
                },
                MenuProps: {
                  PaperProps: {
                    className: "haklai-select-menu",
                    style: { minWidth: 56 }
                  }
                }
              }}
            >
              {currencyOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {COMPACT_CURRENCY_LABELS[option.value] || option.label}
                </MenuItem>
              ))}
            </TextField>
          </FieldShell>
        </Box>
      </StyledCell>
      {showZabanColumn ? (
        validationState.isZabanRelevant ? (
          <StyledCell align="center">
            <Box className={`${classes.zabanCheckboxCellContent} ${classes.zabanFieldWrap}`}>
              {/*
                The zaban cluster is not a single input, so we compose the
                shell manually: the shared status slot on the left (same red
                "!" indicator as every other cell) plus the existing red
                border on the checkbox row when in error. The two error
                affordances are intentionally complementary — the border
                makes it obvious at a glance that the cluster is where the
                issue lives, while the "!" owns the accessible name and
                tooltip message.
              */}
              <Box className={[classes.fieldShell, classes.zabanFieldShell].join(" ")}>
                {renderFieldErrorIndicator(
                  classes,
                  rowCellErrors.zabanAttribution || "",
                  zabanErrorId
                )}
                <Box className={classes.fieldShellInputSlot}>
                  <Box
                    className={[
                      classes.zabanCheckboxField,
                      hasZabanError ? classes.zabanCheckboxFieldError : "",
                      isIgnorableTrailingRow ? classes.optionalTrailingField : ""
                    ].filter(Boolean).join(" ")}
                    onBlurCapture={(event) => {
                      const nextTarget = event.relatedTarget;
                      if (nextTarget && event.currentTarget.contains(nextTarget)) return;
                      touchRowField(expense.id, "zabanAttribution");
                    }}
                    aria-invalid={hasZabanError}
                    aria-describedby={hasZabanError ? zabanErrorId : undefined}
                  >
                    {zabanCheckboxOptions.map((option, optionIndex) => {
                      const checked = selectedZabanValues.includes(option.value);
                      return (
                        <FormControlLabel
                          key={option.value}
                          className={classes.zabanCheckboxOption}
                          labelPlacement="end"
                          control={(
                            <Checkbox
                              size="small"
                              color="primary"
                              name={`expenseZabanAttribution_${expense.id}`}
                              id={`expenseZabanAttribution_${expense.id}_${option.value}`}
                              checked={checked}
                              inputRef={optionIndex === 0 ? setFieldRef(zabanInputRefs, expense.id) : undefined}
                              onChange={(event) => {
                                const nextSelections = event.target.checked
                                  ? [...selectedZabanValues, option.value]
                                  : selectedZabanValues.filter((value) => value !== option.value);
                                handleExpenseFieldChange(
                                  expense.id,
                                  "zabanAttribution",
                                  buildHaklaiZabanAttributionValue(nextSelections)
                                );
                              }}
                            />
                          )}
                          label={option.label}
                        />
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </Box>
          </StyledCell>
        ) : renderNonRelevantCell(`expenseZabanAttribution_${expense.id}`)
      ) : null}
      {showDepreciationColumns && renderExpenseDepreciationResultCell
        ? renderExpenseDepreciationResultCell(expense, `expenseDepreciationResult_${expense.id}`)
        : null}
      {showDepreciationColumns && renderExpenseDepreciationField
        ? renderExpenseDepreciationField(expense, `expenseDepreciation_${expense.id}`)
        : null}
      <StyledCell align="center" className={[classes.actionCell, "haklai-icon-cell"].join(" ")}>
        <Box className={classes.rowActionCellContent}>
          <Tooltip title={HAKLAI_EXPENSE_TOOLTIPS.deleteExpense}>
            <span>
              <IconButton
                size="small"
                aria-label={HAKLAI_EXPENSE_TOOLTIPS.deleteExpense}
                onClick={() => handleRemoveExpense(expense.id)}
              >
                <DeleteOutlineIcon fontSize="small" style={{ color: customColors.red }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </StyledCell>
    </TableRow>
  );
});

HaklaiExpenseRow.displayName = "HaklaiExpenseRow";

export default HaklaiExpenseRow;
